import { TutorialConfig, TutorialTask, SoftwareType } from './types';
import { getTutorialConfig } from './registry';
import axios from 'axios';

// Use full URL in development (when running npm start), relative URL in production (Docker with nginx)
const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'https://api.habitat.inc/v1' : '/api/v1';
const getAuthHeaders = () => {
  const accessCode = localStorage.getItem('accessCode');
  return accessCode ? { Authorization: `Bearer ${accessCode}` } : {};
};

export class TutorialService {
  private config: TutorialConfig;
  private taskDataCache: Map<string, any> = new Map();

  constructor(software: SoftwareType) {
    console.log(`TutorialService: Constructing for software: ${software}`);
    const config = getTutorialConfig(software);
    if (!config) {
      throw new Error(`No tutorial configuration found for software: ${software}`);
    }
    console.log(`TutorialService: Loaded config:`, config);
    this.config = config;
  }

  getConfig(): TutorialConfig {
    return this.config;
  }

  getTasks(): TutorialTask[] {
    return this.config.tasks;
  }

  getSteps() {
    return this.config.steps;
  }

  getTask(index: number): TutorialTask | null {
    return this.config.tasks[index] || null;
  }

  getTaskCount(): number {
    return this.config.tasks.length;
  }

  getCompletionKey(userId: string): string {
    return `${this.config.completionKey}_${userId}`;
  }

  async fetchTaskData(task: TutorialTask): Promise<any> {
    // Check cache first
    const cacheKey = task.runId || task.id;
    if (this.taskDataCache.has(cacheKey)) {
      console.log(`Using cached data for task ${task.id}`);
      return this.taskDataCache.get(cacheKey);
    }

    console.log(`Fetching new data for task ${task.id}`);
    let data;

    if (this.config.dataSource === 'api') {
      if (!task.runId) {
        // Some tasks (like project tours) do not correspond to a run
        return null;
      }
      // Fetch from API (Blender-style)
      try {
        // Fetch the specific problem run from the API
        const runResponse = await axios.get(`${API_BASE_URL}/problem-runs/${task.runId}`, {
          headers: getAuthHeaders()
        });
        
        const runData = runResponse.data;
        
        // Fetch the problem details
        const problemResponse = await axios.get(`${API_BASE_URL}/problems/${runData.problem_id}`, {
          headers: getAuthHeaders()
        });
        
        const problemData = problemResponse.data;
        
        // Also fetch messages for the run
        const messagesResponse = await axios.get(`${API_BASE_URL}/problem-runs/${task.runId}/messages`, {
          headers: getAuthHeaders()
        });
        
        const messages = messagesResponse.data;

        data = {
          run: {
            ...runData,
            problem: problemData
          },
          messages: messages
        };
      } catch (error) {
        console.error(`Failed to fetch task data for ${task.name}:`, error);
        throw error;
      }
    } else if (this.config.dataSource === 'local' && this.config.fetchTaskData) {
      // Use custom fetcher (FreeCAD-style)
      data = await this.config.fetchTaskData(task);
    } else {
      throw new Error(`Unable to fetch data for task: ${task.name}`);
    }

    // Cache the result
    console.log(`Caching data for task ${task.id} with key ${cacheKey}`);
    this.taskDataCache.set(cacheKey, data);
    return data;
  }

  // Get the first step for a given task
  getFirstStepForTask(taskIndex: number): string {
    const taskStepMap: Record<string, string> = {
      'freecad': {
        0: 'welcome',
        1: 'task-2-intro',
        2: 'task-3-intro',
        3: 'task-4-intro',
        4: 'task-5-intro',
      }[taskIndex] || 'welcome',
      'blender': {
        0: 'welcome',
        1: 'task-2-intro',
        2: 'task-3-intro',
        3: 'task-4-intro',
        4: 'task-5-intro',
        5: 'task-6-intro',
        6: 'task-7-intro',
      }[taskIndex] || 'welcome',
      'musescore': {
        0: 'welcome',
        1: 'task-2-intro',
        2: 'task-3-intro',
        3: 'task-4-intro',
        4: 'task-5-intro',
        5: 'task-6-intro',
      }[taskIndex] || 'welcome',
      'kicad': {
        0: 'welcome',
        1: 'obvious-failure-intro',
        2: 'hidden-failure-intro',
        3: 'hidden-success-intro',
      }[taskIndex] || 'welcome',
      'audacity': {
        0: 'audacity-project-welcome',
        1: 'audacity-example-success-intro',
        2: 'audacity-terminal-intro',
        3: 'audacity-combo-intro',
        4: 'audacity-code-fail-intro',
        5: 'audacity-precision-intro',
        6: 'audacity-judgement-intro',
      }[taskIndex] || 'audacity-project-welcome',
      'shotcut': {
        0: 'shotcut-project-welcome',
        1: 'shotcut-obvious-failure-intro',
        2: 'shotcut-obvious-success-intro',
        3: 'shotcut-bad-task-intro',
        4: 'shotcut-ffmpeg-intro',
        5: 'shotcut-timing-intro',
      }[taskIndex] || 'shotcut-project-welcome',
      'gimp': {
        0: 'gimp-project-welcome',
        1: 'gimp-obvious-success-intro',
        2: 'gimp-obvious-failure-intro',
        3: 'gimp-deceptive-failure-intro',
        4: 'gimp-deceptive-success-intro',
        5: 'gimp-judgement-success-intro',
        6: 'gimp-judgement-failure-intro',
      }[taskIndex] || 'gimp-project-welcome',
      'libreoffice': {
        0: 'libreoffice-project-welcome',
        1: 'libreoffice-obvious-success-intro',
        2: 'libreoffice-obvious-failure-intro',
        3: 'libreoffice-deceptive-success-1-intro',
        4: 'libreoffice-deceptive-success-2-intro',
        5: 'libreoffice-deceptive-failure-intro',
        6: 'libreoffice-ambiguous-task-intro',
      }[taskIndex] || 'libreoffice-project-welcome',
      'inkscape': {
        0: 'inkscape-project-welcome',
        1: 'inkscape-obvious-success-intro',
        2: 'inkscape-obvious-failure-intro',
        3: 'inkscape-hidden-success-intro',
        4: 'inkscape-hidden-failure-intro',
        5: 'inkscape-precision-intro',
        6: 'inkscape-ambiguous-intro',
      }[taskIndex] || 'inkscape-project-welcome',
    };

    return taskStepMap[this.config.software] || 'welcome';
  }

  // Get all step IDs for tracking
  getAllStepIds(): string[] {
    return this.config.steps.map(step => step.id);
  }

  // Check if tutorial has been completed
  isCompleted(userId: string): boolean {
    const key = this.getCompletionKey(userId);
    return localStorage.getItem(key) === 'true';
  }

  // Mark tutorial as completed
  markCompleted(userId: string): void {
    const key = this.getCompletionKey(userId);
    localStorage.setItem(key, 'true');
  }

  // Clear tutorial completion
  clearCompletion(userId: string): void {
    const key = this.getCompletionKey(userId);
    localStorage.removeItem(key);
  }

  // Clear all cached data
  clearCache(): void {
    this.taskDataCache.clear();
  }
}
